import { Injectable, inject } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import {
  ExternalDisplayPropertiesProvider,
  ProductContext,
  ProductContextDisplayProperties,
} from 'ish-core/facades/product-context.facade';
import { ProductHelper } from 'ish-core/models/product/product.helper';
import {
  getPresidioWarehouseHeldItem,
  isPresidioWarehouseHeldPricingActive,
  isPresidioWarehouseHeldSku,
} from 'src/app/extensions/presidio-demo/mock/presidio-mock-warehouse-held-inventory';
import { PresidioVariationPreviewService } from 'ish-shared/components/product/product-variation-select/presidio-variation-preview.service';

/**
 * CUSTOMIZATION: Presidio theme override of display properties.
 * - Missing inventory on listing stubs must not hide ATC (treat as in-stock until loaded).
 * - Availability ordering rules:
 *   - In stock → allow order (no qty counts in UI)
 *   - Out of stock with ETA (readyForShipment*) → allow order (backorder)
 *   - Out of stock without ETA → block order; offer notification when applicable
 *   - MVP 2.6.3 warehouse held stock with available qty → allow order (never Notify)
 * - Stock qty/ATC/order-template/quote stay off for unconfigured variation masters and
 *   retail sets; Presidio templates supply configure / select-items CTAs instead.
 * - Availability pill: shown for simple products and retail sets; for variation masters
 *   only after a concrete SKU is selected (configuration complete).
 * - Lead time is not shown as a separate shipment chrome; ETA lives in the availability pill.
 * DIAS baseline remains in the non-theme service file.
 */
@Injectable({ providedIn: 'root' })
export class ProductContextDisplayPropertiesService implements ExternalDisplayPropertiesProvider {
  private variationPreview = inject(PresidioVariationPreviewService);

  setup(
    context$: Observable<Pick<ProductContext, 'product' | 'prices' | 'inventory'>>
  ): Observable<Partial<ProductContextDisplayProperties<false>>> {
    return combineLatest([
      context$,
      this.variationPreview.state$.pipe(startWith(this.variationPreview.snapshot)),
    ]).pipe(
      map(([{ product, prices, inventory }]) => {
        const isMaster = ProductHelper.isMasterProduct(product);
        const isRetail = ProductHelper.isRetailSet(product);
        const isPor = ProductHelper.isPriceOnRequest(product);
        const masterReady = this.variationPreview.isCommerceReady(product?.sku);
        // Prefer loaded inventory; missing inventory must not mean out of stock on PLP
        const inStock = inventory == null ? product?.available !== false : !!inventory.inStock;
        const hasEta =
          product?.readyForShipmentMin != null || product?.readyForShipmentMax != null;
        // MVP 2.6.3 — customer-owned held stock is orderable even when catalog is OOS.
        const warehouseHeld =
          !!product?.sku && isPresidioWarehouseHeldPricingActive() && isPresidioWarehouseHeldSku(product.sku);
        const heldAvailableQty = warehouseHeld ? getPresidioWarehouseHeldItem(product.sku)?.availableQty ?? 0 : 0;
        const hasHeldStock = heldAvailableQty > 0;
        // Backorder allowed when OOS but an ETA exists; held stock also orderable
        const availableToOrder = inStock || (!inStock && hasEta) || hasHeldStock;
        const outOfStockNoEta = !inStock && !hasEta && !hasHeldStock;

        const canBeOrdered = availableToOrder;
        const canBeOrderedNotRetail = canBeOrdered && !isRetail;
        // Masters unlock save actions only after all options are chosen
        const canBeOrderedConfigured = canBeOrderedNotRetail && (!isMaster || masterReady);
        // ATC requires a priced SKU — or MVP 2.4.7 price-on-request (cart then request pricing).
        // MVP 2.6.3 — warehouse held owned stock is $0 but still addable.
        const canBeOrderedWithPrice =
          canBeOrderedConfigured && !isMaster && (!!prices?.salePrice || isPor || warehouseHeld);

        const calc = {
          // Masters: wait until a variation SKU is selected; retail sets & simples: always
          inventory: !isMaster || masterReady,
          quantity: canBeOrderedConfigured,
          variations: ProductHelper.isVariationProduct(product),
          bundleParts: ProductHelper.isProductBundle(product),
          retailSetParts: isRetail,
          // ETA is shown inside the availability pill, not as separate shipment chrome
          shipment: false,
          addToBasket: canBeOrderedWithPrice,
          addToWishlist: !isRetail,
          addToOrderTemplate: canBeOrderedConfigured,
          addToCompare: !isRetail,
          addToQuote: canBeOrderedConfigured,
          // Notify when OOS with no ETA — never for warehouse held products (MVP 2.6.3)
          addToNotification: outOfStockNoEta && !isRetail && !isMaster && !warehouseHeld,
        };

        return Object.entries(calc)
          .filter(([, v]) => v === false)
          .reduce((acc, [k, v]) => ({ ...acc, [k]: v as false }), {});
      })
    );
  }
}
